/**
 * SubmissionDetailPage — page `/submissions/:id`.
 *
 * Vue read-only sur la submission + panneau d'extraction (9.B).
 * - Fetch détail enrichi (`GET /v1/submissions/:id`) → header + files + last
 *   extraction.
 * - Pas de mutations sur la submission elle-même (le PATCH status/notes/etc.
 *   est exposé par l'API mais reste hors scope 9.B).
 * - Lien retour vers le projet rattaché.
 */

import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { ExtractionReviewPanel } from "../../components/extraction/ExtractionReviewPanel";
import { SourceDocument } from "../../components/SourceDocument";
import { ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import { comparisonContext } from "../../lib/comparisonNavigation";
import { useProject } from "../../lib/queries/projects";
import { useSubmission } from "../../lib/queries/submissions";
import { DocumentLinksSection } from "../projects/_parts/DocumentLinksSection";
import { MissingInformationSection } from "../projects/_parts/MissingInformationSection";

import { ComparedSourceValue } from "./_parts/ComparedSourceValue";
import { ExtractionPanel } from "./_parts/ExtractionPanel";
import { RecipientLinkSection } from "./_parts/RecipientLinkSection";

export function SubmissionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);

  const [params] = useSearchParams();
  const comparisonParams = new URLSearchParams(comparisonContext(params));
  const field = comparisonParams.get("field");
  const subQ = useSubmission(id);
  const sub = subQ.data;
  const projQ = useProject(sub?.project_id);

  if (subQ.isLoading) {
    return <p className="p-6 text-sm text-slate-500">{t("app.loading")}</p>;
  }

  if (subQ.error || !sub) {
    return (
      <div className="p-6">
        <p className="text-sm text-red-700">
          {subQ.error instanceof ApiException
            ? getApiErrorMessage(subQ.error, lang) || t("errors.not_found.title")
            : t("errors.not_found.title")}
        </p>
        <Link to="/projects" className="text-brand-700 mt-3 inline-block text-sm hover:underline">
          {t("errors.not_found.back_home")}
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-screen-xl" data-screen-label="Submission Detail">
      {/* Sticky header */}
      <header className="workspace-command-bar py-3">
        <nav aria-label="breadcrumb" className="mb-2 text-sm text-slate-600">
          <Link
            to="/projects"
            className="focus-visible:ring-brand-500 rounded hover:text-slate-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            ← {t("nav.projects")}
          </Link>
          {sub.project_id && projQ.data && (
            <>
              <span aria-hidden="true"> · </span>
              <Link
                to={`/projects/${sub.project_id}`}
                className="focus-visible:ring-brand-500 rounded hover:text-slate-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              >
                {projQ.data.title ?? sub.project_id}
              </Link>
            </>
          )}
          <span aria-hidden="true"> · </span>
          <span>{t("submissions.detail.breadcrumb")}</span>
        </nav>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
          {sub.insurer_name}
          <span className="ml-2 text-sm font-normal text-slate-500">({sub.insurer_code})</span>
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          {t(`submissions.statuses.${sub.status}`, { defaultValue: sub.status })}
          {sub.detection_confidence !== null
            ? ` · ${t("submissions.detail.confidence")}: ${(sub.detection_confidence * 100).toFixed(0)}%`
            : ""}
        </p>
        <nav className="mt-3 flex flex-wrap gap-3 text-sm">
          <Link
            className="underline"
            to={`/intake-events?tab=offers${params.get("receipt_cursor") ? `&cursor=${encodeURIComponent(params.get("receipt_cursor")!)}` : ""}`}
          >
            {t("reception_flow.offers")}
          </Link>
          <Link
            className="underline"
            to={`/projects/${sub.project_id}/comparison${params.get("from") === "comparison" ? comparisonContext(params) : sub.quote_request_id ? `?quote_request_id=${encodeURIComponent(sub.quote_request_id)}` : ""}`}
          >
            {t("reception_flow.compare")}
          </Link>
          {sub.received_via_intake_event_id && (
            <Link className="underline" to={`/intake-events/${sub.received_via_intake_event_id}`}>
              {t("reception_flow.origin")}
            </Link>
          )}
        </nav>
      </header>

      <div className="space-y-5 py-6 lg:py-8">
        <div className="grid items-start gap-4 lg:grid-cols-2">
          <section className="min-w-0 space-y-4 rounded-xl border border-slate-200 bg-white p-4">
            <h2 className="font-semibold">{t("reception_flow.source")}</h2>
            {sub.files.length === 0 && (
              <p className="text-sm text-slate-500">{t("reception_flow.empty")}</p>
            )}
            {sub.files.map((file) => (
              <SourceDocument
                key={file.id}
                fileId={file.id}
                name={file.file_name}
                mimeType={file.mime_type}
              />
            ))}
          </section>
          <div className="min-w-0">
            {field ? (
              <ComparedSourceValue
                projectId={sub.project_id}
                submissionId={sub.id}
                field={field}
                quoteRequestId={comparisonParams.get("quote_request_id") ?? undefined}
              />
            ) : (
              <ExtractionReviewPanel parentKind="submission" parentId={sub.id} />
            )}
          </div>
        </div>
        <details className="rounded-xl border bg-white p-4">
          <summary className="cursor-pointer font-medium">{t("reception_flow.processing")}</summary>
          {/* Panneau d'extraction — PRÉSERVÉ : state machine + polling intacts. */}
          <ExtractionPanel
            submissionId={sub.id}
            files={sub.files}
            initialExtractionId={sub.last_extraction?.id ?? null}
          />
        </details>
        {/* 9.AP.1 — la demande à laquelle cette proposition répond. Placée haut :
            un rattachement absent ou faux fausse la boucle assureur, le filtre du
            comparateur et la réconciliation des demandes d'information. */}
        <RecipientLinkSection submission={sub} />

        {/* Notes (lecture seule pour 9.B) */}
        {sub.notes && (
          <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <header className="border-b border-slate-100 px-5 py-3.5">
              <h2 className="text-base font-semibold text-slate-900">
                {t("submissions.detail.notes")}
              </h2>
            </header>
            <div className="px-5 py-5">
              <p className="whitespace-pre-wrap text-sm text-slate-800">{sub.notes}</p>
            </div>
          </section>
        )}

        {/* 9.L.12 — Informations manquantes scopées submission. Transitions
            broker fulfill / waive / reopen / supersede (backend 9.I.1+9.I.2). */}
        <MissingInformationSection scope="submission" submissionId={sub.id} />

        {/* 9.L.13 — Documents liés à la submission via document_links
            polymorphe `attachable_kind=submission`. Create + Delete broker
            (backend 9.J + asymétrie gating). */}
        <DocumentLinksSection scope="submission" submissionId={sub.id} />
      </div>
    </div>
  );
}
